import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { platform, refresh, session } from "./api";
import type { Me } from "./types";

type Auth = {
  me: Me | undefined;
  booting: boolean;
  loading: boolean;
  authenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (input: { store_name: string; email: string; password: string; name: string; accept_terms: boolean }) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  logout: () => void;
  adopt: (accessToken: string) => void;
};

const Ctx = createContext<Auth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  // booting: la încărcare încercăm să refacem sesiunea din cookie-ul httpOnly (sau din sesiunea de suport).
  // Pe /impersonate nu: acolo sesiunea vine din token-ul de suport, nu din cookie-ul comerciantului.
  const [booting, setBooting] = useState(() => !session.get() && window.location.pathname !== "/impersonate");
  const [authenticated, setAuthenticated] = useState(() => !!session.get());
  useEffect(() => {
    if (!booting) return;
    refresh().then((ok) => { setAuthenticated(ok || !!session.get()); setBooting(false); });
  }, [booting]);
  const me = useQuery({ queryKey: ["me"], queryFn: () => platform.get<Me>("/me"), enabled: authenticated, staleTime: 60_000 });

  const logout = useCallback(() => {
    // Logout-ul revocă refresh token-ul din cookie (o sesiune de suport nu are cookie: doar expiră).
    if (!session.get()?.support) platform.post("/auth/logout", {}).catch(() => {});
    session.clear();
    qc.clear();
    setAuthenticated(false);
  }, [qc]);

  useEffect(() => {
    const on = () => { qc.clear(); setAuthenticated(false); };
    window.addEventListener("auth:logout", on);
    return () => window.removeEventListener("auth:logout", on);
  }, [qc]);

  const value: Auth = {
    me: me.data,
    booting,
    loading: authenticated && me.isLoading,
    authenticated,
    logout,
    async login(email, password) {
      session.set(await platform.post("/auth/login", { email, password }));
      setAuthenticated(true);
    },
    adopt(accessToken) {
      session.set({ access_token: accessToken, expires_at: "" }, true);
      qc.clear();
      setAuthenticated(true);
    },
    // Parola nouă din linkul primit pe email: API-ul întoarce o sesiune nouă (refresh token-ul în cookie).
    async resetPassword(token, password) {
      session.set(await platform.post("/auth/password", { token, password }));
      qc.clear();
      setAuthenticated(true);
    },
    async signup(input) {
      session.set(await platform.post("/signup", input));
      setAuthenticated(true);
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth outside AuthProvider");
  return c;
}

export function useCurrency() {
  return useAuth().me?.store.currency ?? "RON";
}
